import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, Clock, CheckCircle2, ArrowDownLeft, ArrowUpRight, CircleDashed, Inbox, ClipboardCheck, Users, Building2, Minus, Send, AlertTriangle, CalendarClock } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total: 0,
    not_yet_initiated: 0,
    just_received: 0,
    board_approved: 0,
    ec_approved: 0,
    business_meeting_approved: 0,
    sent: 0,
    completed: 0,
    completed_percentage: 0,
    transfer_in: 0,
    transfer_out: 0,
    transfer_na: 0,
    overdue_count: 0,
  });
  const [monthlyTransfers, setMonthlyTransfers] = useState([]);
  const [overdueLetters, setOverdueLetters] = useState([]);
  const [upcomingLetters, setUpcomingLetters] = useState([]);
  const [recentLetters, setRecentLetters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, lettersRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`),
          axios.get(`${API}/letters`),
        ]);
        setStats(statsRes.data.stats);
        setMonthlyTransfers(statsRes.data.monthly_transfers);
        setOverdueLetters(statsRes.data.overdue_letters || []);
        setUpcomingLetters(statsRes.data.upcoming_letters || []);
        setRecentLetters(lettersRes.data.slice(0, 5));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleStatClick = (filterType, filterValue) => {
    if (filterType && filterValue) {
      navigate(`/letters?${filterType}=${encodeURIComponent(filterValue)}`);
    } else {
      navigate('/letters');
    }
  };

  const statCards = [
    {
      title: "Total Letters",
      value: stats.total,
      icon: Mail,
      color: "bg-blue-900 text-white",
      filterType: null,
      filterValue: null,
    },
    {
      title: "Not Yet Initiated",
      value: stats.not_yet_initiated,
      icon: CircleDashed,
      color: "bg-slate-100 text-slate-700 border border-slate-200",
      filterType: "status",
      filterValue: "Not Yet Initiated",
    },
    {
      title: "Just Received",
      value: stats.just_received,
      icon: Inbox,
      color: "bg-sky-50 text-sky-700 border border-sky-200",
      filterType: "status",
      filterValue: "Just Received",
    },
    {
      title: "Board Approved",
      value: stats.board_approved,
      icon: ClipboardCheck,
      color: "bg-violet-50 text-violet-700 border border-violet-200",
      filterType: "status",
      filterValue: "Board Approved",
    },
    {
      title: "EC Approved",
      value: stats.ec_approved,
      icon: Users,
      color: "bg-indigo-50 text-indigo-700 border border-indigo-200",
      filterType: "status",
      filterValue: "EC Approved",
    },
    {
      title: "BM Approved",
      value: stats.business_meeting_approved,
      icon: Building2,
      color: "bg-amber-50 text-amber-700 border border-amber-200",
      filterType: "status",
      filterValue: "Business Meeting Approved",
    },
    {
      title: "Sent",
      value: stats.sent,
      icon: Send,
      color: "bg-cyan-50 text-cyan-700 border border-cyan-200",
      filterType: "status",
      filterValue: "Sent",
    },
    {
      title: "Completed",
      value: `${stats.completed_percentage}%`,
      subValue: `(${stats.completed} of ${stats.total})`,
      icon: CheckCircle2,
      color: "bg-emerald-50 text-emerald-700 border border-emerald-200",
      filterType: "status",
      filterValue: "Completed",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight">
          Dashboard
        </h1>
        <p className="text-slate-500 mt-2 font-body">
          Overview of your letter status management
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-8" data-testid="stats-grid">
        {statCards.map((stat) => (
          <Card
            key={stat.title}
            onClick={() => handleStatClick(stat.filterType, stat.filterValue)}
            className={`stat-card shadow-sm border border-slate-100 cursor-pointer hover:shadow-md transition-all duration-200 ${stat.color.includes('bg-blue') ? stat.color : ''}`}
            data-testid={`stat-${stat.title.toLowerCase().replace(/\s/g, '-')}`}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className={`text-xs uppercase tracking-wider font-semibold font-body ${stat.color.includes('bg-blue') ? 'text-blue-100' : 'text-slate-500'}`}>
                {stat.title}
              </CardTitle>
              <div className={`p-2 rounded-lg ${stat.color.includes('bg-blue') ? 'bg-blue-800' : stat.color}`}>
                <stat.icon size={18} />
              </div>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold font-heading ${stat.color.includes('bg-blue') ? 'text-white' : 'text-slate-900'}`}>
                {stat.value}
              </div>
              {stat.subValue && (
                <p className={`text-xs mt-1 ${stat.color.includes('bg-blue') ? 'text-blue-200' : 'text-slate-500'}`}>
                  {stat.subValue}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Monthly Transfers Chart */}
      <Card className="shadow-sm border border-slate-100 mb-8" data-testid="transfers-chart-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl font-semibold text-slate-900">
            Monthly Transfer Activity
          </CardTitle>
          <p className="text-sm text-slate-500 font-body">Letters received and sent over the last 24 months (based on date received)</p>
        </CardHeader>
        <CardContent>
          <div className="w-full h-[360px]" data-testid="monthly-line-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthlyTransfers} margin={{ top: 10, right: 24, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  tickLine={{ stroke: '#e2e8f0' }}
                  interval="preserveStartEnd"
                  angle={-35}
                  textAnchor="end"
                  height={50}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickLine={{ stroke: '#e2e8f0' }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '6px' }} />
                <Line
                  type="monotone"
                  dataKey="transfer_in"
                  name="Transfer In"
                  stroke="#059669"
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
                <Line
                  type="monotone"
                  dataKey="transfer_out"
                  name="Transfer Out"
                  stroke="#d97706"
                  strokeWidth={2.5}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4 flex-wrap">
            <div 
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => handleStatClick('type_of_transfer', 'In')}
              data-testid="transfer-in-link"
            >
              <ArrowDownLeft className="text-emerald-600" size={18} />
              <span className="text-sm font-medium text-slate-700">{stats.transfer_in} Transfer In</span>
            </div>
            <div 
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => handleStatClick('type_of_transfer', 'Out')}
              data-testid="transfer-out-link"
            >
              <ArrowUpRight className="text-amber-600" size={18} />
              <span className="text-sm font-medium text-slate-700">{stats.transfer_out} Transfer Out</span>
            </div>
            <div 
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => handleStatClick('type_of_transfer', 'N/A')}
              data-testid="transfer-na-link"
            >
              <Minus className="text-slate-500" size={18} />
              <span className="text-sm font-medium text-slate-700">{stats.transfer_na} N/A</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Follow-ups This Week */}
      {upcomingLetters.length > 0 && (
        <Card className="shadow-sm border border-amber-200 mb-8 bg-amber-50" data-testid="upcoming-letters-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CalendarClock className="text-amber-700" size={22} />
              <CardTitle className="font-heading text-xl font-semibold text-amber-900">
                Follow-ups This Week ({upcomingLetters.length})
              </CardTitle>
            </div>
            <p className="text-sm text-amber-700 font-body">Letters with follow-up dates in the next 7 days</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-amber-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-amber-800 font-body">Folio</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-amber-800 font-body">Name</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-amber-800 font-body">Church</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-amber-800 font-body">Status</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-amber-800 font-body">Follow-up Date</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-amber-800 font-body">When</th>
                  </tr>
                </thead>
                <tbody>
                  {upcomingLetters.map((letter) => {
                    const whenLabel = letter.days_until === 0 ? "Today" : letter.days_until === 1 ? "Tomorrow" : `In ${letter.days_until} days`;
                    return (
                      <tr
                        key={letter.id}
                        className="border-b border-amber-100 hover:bg-amber-100 transition-colors cursor-pointer"
                        onClick={() => navigate(`/letters?edit=${encodeURIComponent(letter.id)}`)}
                        data-testid={`upcoming-letter-${letter.id}`}
                      >
                        <td className="py-3 px-4 text-sm font-semibold text-amber-900 font-body">{letter.folio}</td>
                        <td className="py-3 px-4 text-sm text-amber-900 font-body">{letter.name}</td>
                        <td className="py-3 px-4 text-sm text-amber-800 font-body">{letter.church}</td>
                        <td className="py-3 px-4">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium font-body bg-amber-100 text-amber-800 border border-amber-300">
                            {letter.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-amber-800 font-body">{letter.follow_up_date}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold font-body ${letter.days_until <= 1 ? 'bg-amber-700 text-white' : 'bg-amber-200 text-amber-900'}`}>
                            {whenLabel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Overdue Letters */}
      {overdueLetters.length > 0 && (
        <Card className="shadow-sm border border-red-200 mb-8 bg-red-50" data-testid="overdue-letters-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-red-600" size={22} />
              <CardTitle className="font-heading text-xl font-semibold text-red-800">
                Overdue Letters ({overdueLetters.length})
              </CardTitle>
            </div>
            <p className="text-sm text-red-600 font-body">These letters have passed their follow-up date and need attention</p>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-red-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-red-700 font-body">Folio</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-red-700 font-body">Name</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-red-700 font-body">Church</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-red-700 font-body">Status</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-red-700 font-body">Follow-up Date</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-red-700 font-body">Days Overdue</th>
                  </tr>
                </thead>
                <tbody>
                  {overdueLetters.map((letter) => (
                    <tr 
                      key={letter.id} 
                      className="border-b border-red-100 hover:bg-red-100 transition-colors cursor-pointer" 
                      onClick={() => navigate(`/letters?edit=${encodeURIComponent(letter.id)}`)}
                      data-testid={`overdue-letter-${letter.id}`}
                    >
                      <td className="py-3 px-4 text-sm font-semibold text-red-900 font-body">{letter.folio}</td>
                      <td className="py-3 px-4 text-sm text-red-800 font-body">{letter.name}</td>
                      <td className="py-3 px-4 text-sm text-red-700 font-body">{letter.church}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium font-body bg-red-100 text-red-700 border border-red-300">
                          {letter.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-red-700 font-body">{letter.follow_up_date}</td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold font-body bg-red-600 text-white">
                          {letter.days_overdue} days
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Letters */}
      <Card className="shadow-sm border border-slate-100" data-testid="recent-letters-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl font-semibold text-slate-900">
            Recent Letters
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentLetters.length === 0 ? (
            <div className="empty-state" data-testid="empty-recent">
              <img
                src="https://images.unsplash.com/photo-1642437832852-4f6192df4548?crop=entropy&cs=srgb&fm=jpg&q=85&w=300"
                alt="No letters"
                className="mx-auto mb-4"
              />
              <p className="text-slate-500 font-body">No letters yet. Add your first letter to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-500 font-body">Folio</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-500 font-body">Name</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-500 font-body">Type</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-slate-500 font-body">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLetters.map((letter) => (
                    <tr key={letter.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate('/letters')} data-testid={`recent-letter-${letter.id}`}>
                      <td className="py-3 px-4 text-sm font-medium text-slate-900 font-body">{letter.folio}</td>
                      <td className="py-3 px-4 text-sm text-slate-600 font-body">{letter.name}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium font-body ${
                          letter.type_of_transfer === 'In' 
                            ? 'bg-emerald-50 text-emerald-700' 
                            : letter.type_of_transfer === 'Out'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {letter.type_of_transfer === 'In' ? <ArrowDownLeft size={12} /> : letter.type_of_transfer === 'Out' ? <ArrowUpRight size={12} /> : <Minus size={12} />}
                          {letter.type_of_transfer}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium font-body border ${
                          letter.status === 'Completed' 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                            : letter.status === 'Just Received'
                            ? 'bg-sky-50 text-sky-700 border-sky-200'
                            : letter.status === 'Board Approved'
                            ? 'bg-violet-50 text-violet-700 border-violet-200'
                            : letter.status === 'EC Approved'
                            ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                            : letter.status === 'Business Meeting Approved'
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : letter.status === 'Sent'
                            ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
                            : 'bg-slate-100 text-slate-600 border-slate-200'
                        }`}>
                          {letter.status === 'Completed' ? <CheckCircle2 size={12} /> 
                            : letter.status === 'Just Received' ? <Inbox size={12} />
                            : letter.status === 'Board Approved' ? <ClipboardCheck size={12} />
                            : letter.status === 'EC Approved' ? <Users size={12} />
                            : letter.status === 'Business Meeting Approved' ? <Building2 size={12} />
                            : letter.status === 'Sent' ? <Send size={12} />
                            : <CircleDashed size={12} />}
                          {letter.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
